// mailFolderTree.ts — Aufbereitung des Graph-Ordnerbaums für die Navigation.
//
// Der Baum wird vom Backend vollständig geliefert (`GET /mail/folders`, kein
// per-Ordner-Filter) und liegt im MailContext bereits vor.
//
// Darstellung wie in Outlook: Die fünf Standard-Ordner stehen oben in fester
// Reihenfolge, darunter die übrigen Ordner — alle auf DERSELBEN Ebene, denn sie
// sind gleichrangig. Nur echte Unterordner werden eingerückt, und zwar unter
// ihrem Elternteil, das dafür einen Aufklapp-Pfeil bekommt.
//
// (Die erste Fassung zog Unterordner eine Ebene hoch und rückte alle
// zusätzlichen Ordner ein — dadurch sahen eigenständige Ordner wie Unterordner
// des letzten Standard-Eintrags aus.)

import type { MailFolder } from "../../services/graphMailService";

/** Ordner mit Einrücktiefe für eine flache Darstellung. */
export interface FlatFolder {
    folder: MailFolder;
    depth: number;
}

/** Baum in eine flache, eingerückte Liste überführen (Vorordnung). */
export function flattenFolders(nodes: MailFolder[], depth = 0): FlatFolder[] {
    const out: FlatFolder[] = [];
    for (const n of nodes) {
        out.push({ folder: n, depth });
        if (n.children?.length) out.push(...flattenFolders(n.children, depth + 1));
    }
    return out;
}

/**
 * Namen der Standard-Ordner je Alias, in Deutsch und Englisch — das
 * Postfach-Gebietsschema kann pro Nutzer verschieden sein.
 *
 * Für den Posteingang ist der Namensabgleich nur ein Notnagel: den markiert das
 * Backend zuverlässig über `wellKnownName='inbox'`, darauf wird zuerst geprüft.
 * Für die übrigen vier liefert Graph kein `wellKnownName` mit.
 */
const STANDARD_FOLDER_NAMES: Record<string, string[]> = {
    inbox: ["posteingang", "inbox"],
    drafts: ["entwürfe", "entwuerfe", "drafts"],
    sentitems: ["gesendete elemente", "gesendet", "sent items", "sent"],
    deleteditems: [
        "gelöschte elemente",
        "gelöscht",
        "geloeschte elemente",
        "deleted items",
        "deleted",
    ],
    archive: ["archiv", "archive"],
};

const norm = (s: string) => s.trim().toLowerCase();

/** Zu welchem Standard-Alias gehört dieser Ordner — oder zu keinem? */
export function standardAliasOf(folder: MailFolder): string | null {
    if (folder.wellKnownName === "inbox") return "inbox";
    const name = norm(folder.displayName);
    for (const [alias, namen] of Object.entries(STANDARD_FOLDER_NAMES)) {
        if (namen.includes(name)) return alias;
    }
    return null;
}

/** Steht dieser Ordner in der Navigation bereits als fester Eintrag? */
export function isStandardFolder(folder: MailFolder): boolean {
    return standardAliasOf(folder) !== null;
}

export interface MailMenuTree {
    /**
     * Unterordner je Standard-Alias. Sie hängen im Menü unter ihrem
     * Standard-Eintrag, damit die Verschachtelung der Wirklichkeit entspricht —
     * ein Unterordner des Posteingangs gehört unter den Posteingang.
     */
    standardChildren: Record<string, MailFolder[]>;
    /**
     * Übrige Ordner der obersten Ebene, samt ihren Unterordnern. Sie stehen auf
     * DERSELBEN Ebene wie die Standard-Ordner — sie sind gleichrangig, nicht
     * untergeordnet.
     */
    additional: MailFolder[];
}

/**
 * Teilt den Baum in „Unterordner der Standard-Ordner" und „alles Übrige" auf.
 *
 * Die Standard-Ordner selbst tauchen im Ergebnis nicht auf: Ihre Zeilen sind
 * fest verdrahtet, weil an ihren Alias-Schlüsseln (`inbox`, `drafts`, …) der
 * Ordner-Wechsel in MailView hängt. Nur ihre KINDER werden hier zugeordnet.
 */
export function buildMailMenuTree(tree: readonly MailFolder[]): MailMenuTree {
    const { aliasNodes, additional } = gruppiereNachAlias(tree);
    const standardChildren: Record<string, MailFolder[]> = {};
    for (const [alias, nodes] of Object.entries(aliasNodes)) {
        // Mehrere Treffer auf denselben Alias (etwa „Archiv" und „Archive")
        // zusammenführen, statt den ersten gewinnen zu lassen.
        standardChildren[alias] = nodes.flatMap((n) => n.children ?? []);
    }
    return { standardChildren, additional };
}

/**
 * Die oberste Baumebene in „Knoten je Standard-Alias" und „alles Übrige"
 * zerlegen — die gemeinsame Grundlage von `buildMailMenuTree` und
 * `buildMailFolderOrder`.
 *
 * Bewusst die KNOTEN und nicht nur deren Kinder: `buildMailFolderOrder` braucht
 * die Ungelesen-Zahlen aller Alias-Treffer, und die stehen am Knoten selbst.
 */
function gruppiereNachAlias(tree: readonly MailFolder[]): {
    aliasNodes: Record<string, MailFolder[]>;
    additional: MailFolder[];
} {
    const aliasNodes: Record<string, MailFolder[]> = {};
    const additional: MailFolder[] = [];
    for (const node of tree) {
        const alias = standardAliasOf(node);
        if (alias) aliasNodes[alias] = [...(aliasNodes[alias] ?? []), node];
        else additional.push(node);
    }
    return { aliasNodes, additional };
}

/** Hat der Ordner Unterordner, die sich aufklappen lassen? */
export function hasChildren(folder: MailFolder): boolean {
    // T-65: auch ein Ordner, dessen Kinder das Backend nicht mehr geladen hat
    // (Call-Budget erschöpft), hat Unterordner — `childFolderCount` sagt es.
    return (folder.children?.length ?? 0) > 0 || (folder.childFolderCount ?? 0) > 0;
}

/** T-65: Graph meldet Unterordner, geladen wurden aber keine. */
export function kinderNichtGeladen(folder: MailFolder): boolean {
    return (folder.children?.length ?? 0) === 0 && (folder.childFolderCount ?? 0) > 0;
}

/**
 * Bekommt dieses Postfach den VOLLEN Ordnerbaum oder nur die fünf
 * Standard-Ordner?
 *
 * Beim eigenen Postfach immer. Bei fremden nur für Admins — die
 * Unterordnerstruktur eines Kollegen-Postfachs samt Namen geht andere
 * Leseberechtigte nichts an, und Ordnernamen sind mitunter aussagekräftig
 * („Kündigungen", „Krankmeldung Müller").
 *
 * Ausnahme schaden@: seine Unterordner sind die gemeinsame Ablage aller, die
 * dort arbeiten — wer es lesen darf, sieht den vollen Baum (05.10.2026: einer
 * Mitarbeiterin fehlten dort alle eigenen Ordner). Welche Postfächer das sind,
 * legt das Backend fest (`vollerOrdnerbaum`); cbb@ gehört bewusst NICHT dazu.
 *
 * Anlass der Admin-Ausnahme: In cll@courte.com liegt ein eigener
 * Wiedervorlage-Ordner „Erneut erinnern aktiviert" mit 58 Mails. Für einen
 * Admin mit Leserecht existierte er in der Oberfläche schlicht nicht — er stand
 * in keiner Liste und war nicht erreichbar.
 *
 * Das Backend filtert Ordner NICHT: `GET /mail/folders` liefert den vollen Baum
 * an jeden, der das Postfach lesen darf. Diese Regel ist also eine Frage der
 * Darstellung, keine Zugriffskontrolle — wer sie für eine solche hält, sichert
 * an der falschen Stelle.
 */
export function zeigeVollenBaum(opts: {
    isOwn?: boolean;
    isAdmin?: boolean;
    vollerOrdnerbaum?: boolean;
}): boolean {
    return opts.isOwn === true || opts.isAdmin === true || opts.vollerOrdnerbaum === true;
}

// ── Cluster POSTFACH (G5): EINE Ordner-Reihenfolge für beide Orte ───────────
//
// Bis hierher gab es zwei Ordnungen für dieselben Ordner: die linke Navigation
// stellte die fünf Standard-Ordner fest nach oben, das Verschieben-Menü ließ den
// rohen Graph-Baum durchlaufen (Standard- und eigene Ordner alphabetisch
// gemischt). `buildMailFolderOrder` ist ab jetzt die einzige Quelle — Navigation
// UND „Verschieben nach…" laufen darüber.

/**
 * Die fünf Standard-Ordner in fester Reihenfolge, mit ihrer deutschen
 * Beschriftung. Bewusst Daten ohne JSX (die Icons bleiben in der Navigation),
 * damit dieses Modul ohne React testbar bleibt.
 *
 * `alias` ist zugleich ein gültiges `destinationId` für den Graph-Move
 * (Well-Known-Name) und der Schlüssel, an dem der Ordner-Wechsel in MailView
 * hängt.
 */
export const STANDARD_FOLDER_ORDER: readonly { alias: string; label: string }[] = [
    { alias: "inbox", label: "Posteingang" },
    { alias: "drafts", label: "Entwürfe" },
    { alias: "sentitems", label: "Gesendet" },
    { alias: "deleteditems", label: "Gelöscht" },
    { alias: "archive", label: "Archiv" },
];

// ── TERMINE (P6): der virtuelle Ordner ──────────────────────────────────────
//
// „Termine" ist KEIN Ordner im Postfach, sondern eine SICHT: dieselben Mails
// wie im Posteingang, gesiebt auf Einladungen/Zusagen/Absagen. Es wird nichts
// verschoben und nichts gelöscht — die Nachrichten bleiben zusätzlich dort, wo
// sie liegen.
//
// Daraus folgt die eine Regel, die dieses Modul durchsetzen muss: Ein virtueller
// Eintrag ist NIE ein gültiges Verschiebe-Ziel. Deshalb erscheint er nur, wenn
// ein Aufrufer ihn ausdrücklich anfordert (`virtualTermine`), und trägt
// `virtual: true`, damit ein Ziel-Menü ihn erkennen kann, ohne den Schlüssel zu
// kennen.

/** Schlüssel des virtuellen Termine-Ordners (zugleich der `activeFolder`-Wert). */
export const TERMINE_FOLDER_KEY = "termine";

/** Ist dieser Ordner-Schlüssel eine SICHT statt eines echten Graph-Ordners? */
export function isVirtualFolderKey(key: string): boolean {
    return key === TERMINE_FOLDER_KEY;
}

/** Eine Zeile der aufbereiteten Ordnerliste (Navigation wie Verschieben-Menü). */
export interface MailFolderEntry {
    /**
     * Der Wert, mit dem gewechselt bzw. verschoben wird: bei den fünf
     * Standardzeilen der Alias, sonst die opake Graph-Ordner-Id. Beides ist ein
     * gültiges `destinationId` für `POST /mail/messages/:id/move`.
     */
    key: string;
    /**
     * `key` des Elternknotens, bei Ebene 0 `null`. Ohne dieses Feld könnte die
     * Sidebar aus der flachen Liste nicht mehr ableiten, welche Äste
     * eingeklappt und damit unsichtbar sind.
     */
    parentKey: string | null;
    label: string;
    depth: number;
    /** Standardzeile → ihr Alias; eigener Ordner → `null`. */
    alias: string | null;
    /** Der echte Graph-Knoten (bei Standardzeilen der ERSTE Alias-Treffer). */
    folder: MailFolder | null;
    /** Ungelesen-Zahl; bei Standardzeilen die SUMME aller Alias-Treffer. */
    unread: number;
    hasChildren: boolean;
    /**
     * T-65: Der Ordner hat laut Graph Unterordner, der Baum enthält aber keine
     * (gekappter Baum). Die Sidebar sagt das beim Aufklappen offen, statt
     * nichts zu zeigen.
     */
    kinderFehlen?: boolean;
    /**
     * TERMINE (P6): true = SICHT, kein Graph-Ordner. Solche Zeilen dürfen in
     * einem Verschiebe-Menü nicht auftauchen — `destinationId` gäbe es dafür
     * nicht, der Graph-Move liefe ins Leere. Fehlt das Feld, ist die Zeile ein
     * echter Ordner (Bestandsverhalten).
     */
    virtual?: boolean;
}

/**
 * Ordnerbaum in die Reihenfolge des Postfachs bringen: erst die fünf
 * Standard-Ordner (immer, auch bei leerem Baum — sie sind gültige
 * Well-Known-Ziele), jeweils mit ihren echten Unterordnern eingerückt darunter,
 * danach die übrigen Ordner der obersten Ebene auf derselben Ebene samt ihren
 * Unterordnern.
 *
 * Ausgabe in Vorordnung: ein Elternteil steht IMMER vor seinen Kindern. Darauf
 * verlässt sich der Sichtbarkeitsfilter der Navigation.
 *
 * `includeCustom: false` liefert nur die fünf Standardzeilen ohne Kinder — so
 * zeigt die Navigation es heute für fremde/delegierte Postfächer.
 */
export function buildMailFolderOrder(
    tree: readonly MailFolder[],
    opts?: { includeCustom?: boolean; virtualTermine?: { unread: number } },
): MailFolderEntry[] {
    const includeCustom = opts?.includeCustom !== false;
    const { aliasNodes, additional } = gruppiereNachAlias(tree);
    const out: MailFolderEntry[] = [];

    const anhaengen = (
        nodes: readonly MailFolder[],
        parentKey: string | null,
        depth: number,
    ): void => {
        for (const n of nodes) {
            out.push({
                key: n.id,
                parentKey,
                label: n.displayName,
                depth,
                alias: null,
                folder: n,
                unread: n.unreadItemCount ?? 0,
                hasChildren: hasChildren(n),
                kinderFehlen: kinderNichtGeladen(n),
            });
            if (n.children?.length) anhaengen(n.children, n.id, depth + 1);
        }
    };

    for (const std of STANDARD_FOLDER_ORDER) {
        const treffer = aliasNodes[std.alias] ?? [];
        // Kinder aus ALLEN Alias-Treffern (Merge-Logik von buildMailMenuTree) —
        // „erster Treffer gewinnt" ließe die Unterordner des zweiten
        // verschwinden und meldete fälschlich `hasChildren=false`.
        const kinder = includeCustom ? treffer.flatMap((n) => n.children ?? []) : [];
        const kinderFehlen =
            includeCustom && kinder.length === 0 && treffer.some((n) => (n.childFolderCount ?? 0) > 0);
        out.push({
            key: std.alias,
            parentKey: null,
            label: std.label,
            depth: 0,
            alias: std.alias,
            // Nur als Nachweis mitgeführt; unread/hasChildren kommen bewusst
            // NICHT aus diesem einen Knoten.
            folder: treffer[0] ?? null,
            unread: treffer.reduce((s, n) => s + (n.unreadItemCount ?? 0), 0),
            hasChildren: kinder.length > 0 || kinderFehlen,
            kinderFehlen,
        });
        anhaengen(kinder, std.alias, 1);
    }

    // TERMINE (P6): die Sicht steht zwischen den Standard- und den eigenen
    // Ordnern — gleichrangig, aber ohne Graph-Knoten. Sie erscheint NUR auf
    // ausdrückliche Anforderung; das Verschieben-Menü fragt sie nicht an und
    // kann sie deshalb gar nicht als Ziel anbieten.
    if (opts?.virtualTermine) {
        out.push({
            key: TERMINE_FOLDER_KEY,
            parentKey: null,
            label: "Termine",
            depth: 0,
            alias: null,
            folder: null,
            unread: Math.max(0, opts.virtualTermine.unread),
            hasChildren: false,
            virtual: true,
        });
    }

    // Die übrigen Ordner sind GLEICHRANGIG zu den Standard-Ordnern, also depth 0
    // und ohne Elternteil — nicht eingerückt unter den letzten Standard-Eintrag.
    if (includeCustom) anhaengen(additional, null, 0);

    return out;
}

/**
 * T-264 (Ikram Cetinkaya, 08.10.2026): „in den Unterordnern einsehen, wie viele
 * Mails drin sind". Die Zahl an einem EIGENEN Ordner ist die GESAMTZAHL
 * (`totalItemCount`), nicht die ungelesenen — eine Ungelesen-Zahl gibt es
 * weiterhin nur am Posteingang (Entscheidung #1089, siehe `inboxUnread` im
 * MailContext). Die Navigation zeigt sie deshalb grau statt als Gold-Pille.
 *
 * `null` = keine Zahl: an den fünf Standard-Ordnern (dort ändert sich nichts),
 * an der virtuellen Termine-Sicht (kein Graph-Ordner) und an leeren Ordnern.
 */
export function ordnerGesamtzahl(entry: MailFolderEntry): number | null {
    if (entry.alias !== null || entry.virtual || !entry.folder) return null;
    const gesamt = entry.folder.totalItemCount ?? 0;
    return gesamt > 0 ? gesamt : null;
}

/**
 * Aus der flachen Liste die tatsächlich sichtbaren Zeilen filtern: Ebene 0 immer,
 * ein Kind nur, wenn sein Elternteil selbst sichtbar UND aufgeklappt ist.
 *
 * Diese Funktion ist der Ersatz für das frühere rekursive Rendern in der
 * Navigation, das Kinder gar nicht erst erzeugte, solange der Elternteil zu war.
 * Sie steht bewusst HIER und nicht in der Komponente: der Aufklapp-Zustand ist
 * die eine Stelle, an der der Umbau auf die Flachliste stillschweigend kaputt
 * gehen könnte, und so lässt er sich ohne DOM prüfen.
 *
 * Setzt Vorordnung voraus (`buildMailFolderOrder` liefert sie): der Elternteil
 * steht immer fest, bevor sein Kind an die Reihe kommt.
 */
export function visibleFolderRows(
    entries: readonly MailFolderEntry[],
    istAufgeklappt: (key: string) => boolean,
): MailFolderEntry[] {
    const sichtbar = new Set<string>();
    const out: MailFolderEntry[] = [];
    for (const e of entries) {
        const zeigen =
            e.parentKey === null || (sichtbar.has(e.parentKey) && istAufgeklappt(e.parentKey));
        if (!zeigen) continue;
        sichtbar.add(e.key);
        out.push(e);
    }
    return out;
}
